import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
import {RemoveAction} from '@wandb/weave/components/Tag';
import {RemovableAlias} from '@wandb/weave/components/Tag/Alias';
import React, {useMemo, useState} from 'react';
import {useLocation} from 'react-router';
import {useHistory} from 'react-router-dom';
import {components, MultiValue, MultiValueProps} from 'react-select';

import {useMediaKeys} from '../../../../PanelMediaBrowser/hooks/useMediaKeys';
import {SUPPORTED_MEDIA_TYPES} from '../../../../PanelMediaBrowser/useGetIsMediaPanelSettingsDrawerSupportedForPanel';
import {keysToTitle} from '../../../../PanelMediaBrowser/utils/getTitleFromConfig';
import {isValidMediaKeyOption} from '../../../../PanelMediaBrowser/utils/isValidMediaKeyOption';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsDrawerContext} from '../MediaSettingsDrawerContextProvider';
type MediaKeyOption = {
  value: string | undefined;
  displayText: string;
};

const MediaKeyMultiValue = (props: MultiValueProps<MediaKeyOption, true>) => {
  return (
    <components.MultiValue
      {...props}
      components={{
        Container: () => (
          <RemovableAlias
            color="moon"
            label={props.data.displayText}
            removeAction={
              <RemoveAction
                {...props.removeProps}
                data-test="remove-media-key"
              />
            }
            truncatedPart="middle"
          />
        ),
        Label: () => null,
        Remove: () => null,
      }}
    />
  );
};

export const MediaKeys = () => {
  const history = useHistory();
  const {config, updateConfig} = useMediaSettingsDrawerContext();
  // Local state to control the dropdown so we don't have to wait for the updateConfig call to propagate
  const [keys, setKeys] = useState(config.mediaKeys ?? []);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const {allMediaKeys, keyTypes} = useMediaKeys();
  const mediaKeysOptions = useMemo(
    () =>
      allMediaKeys
        .filter(key =>
          isValidMediaKeyOption(key, keyTypes, keys, SUPPORTED_MEDIA_TYPES)
        )
        .map(k => ({value: k, displayText: k})),
    [allMediaKeys, keyTypes, keys]
  );
  const selectedOptions = useMemo(
    () => mediaKeysOptions.filter(k => keys.includes(k.value)),
    [mediaKeysOptions, keys]
  );

  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Media key(s)"
        tooltip="Keys for media per step. Log all keys for the same step in a single log() call."
        configField="mediaKeys"
      />
      <Select<MediaKeyOption, true>
        aria-label="Select Media Key"
        size="variable"
        value={selectedOptions}
        options={mediaKeysOptions}
        placeholder="Select Media Key"
        isMulti
        isSearchable
        closeMenuOnSelect={false}
        onChange={(value: MultiValue<MediaKeyOption>) => {
          const newValues = value
            .map(v => v.value)
            .filter(isNotNullOrUndefined);

          setKeys(newValues);
          // Don't save to the panel with empty keys
          if (newValues.length > 0) {
            const panelDisplayName = searchParams.get('panelDisplayName');
            const defaultChartTitle = keysToTitle(newValues);
            // Update the URL for the panel display name if on a specific panel page
            if (
              panelDisplayName &&
              config.chartTitle == null &&
              panelDisplayName !== defaultChartTitle
            ) {
              searchParams.set('panelDisplayName', defaultChartTitle);
              history.replace(
                `${location.pathname}?${searchParams.toString()}`
              );
            }
            updateConfig({mediaKeys: newValues});
          }
        }}
        getOptionLabel={opt => opt.displayText}
        components={{
          MultiValue: MediaKeyMultiValue,
        }}
      />
    </div>
  );
};
